import { type RequireExactlyOne } from 'type-fest';

import { type Common } from '../common.js';
import {
  type CreateValues,
  type PracticePreference,
  type PracticePreferenceHistory,
  type PreferenceOptions,
  type Preferences,
  type UpdateValues,
} from '../models/practice/preference.js';

interface PreferenceResponse {
  preferences?: PracticePreference;
  preferenceOptions: PreferenceOptions;
}

export class API {
  common: Common;
  baseEndpoint = '/practice-preferences';

  constructor(common: Common) {
    this.common = common;
  }

  async findAllByOrganizationId(organizationId: string): Promise<{
    practicePreferences: PracticePreference[];
    practiceGroupPreferences: PracticePreference[];
  }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/all`, {
      organizationId,
    });
    return await this.common.getJSON(url);
  }

  async findAllByPracticeGroupId(practiceGroupId: string): Promise<{
    practicePreferences: PracticePreference[];
  }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/all`, {
      practiceGroupId,
    });
    return await this.common.getJSON(url);
  }

  async findOneByPracticeId(practiceId: string): Promise<PreferenceResponse> {
    const url = this.common.constructPath(this.baseEndpoint, {
      practiceId,
    });
    return await this.common.getJSON(url);
  }

  async findOneByPracticeGroupId(practiceGroupId: string): Promise<PreferenceResponse> {
    const url = this.common.constructPath(this.baseEndpoint, {
      practiceGroupId,
    });
    return await this.common.getJSON(url);
  }

  async findOneByOrganizationId(organizationId: string): Promise<PreferenceResponse> {
    const url = this.common.constructPath(this.baseEndpoint, {
      organizationId,
    });
    return await this.common.getJSON(url);
  }

  async findHistory(practicePreferenceId: string): Promise<PracticePreferenceHistory[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${practicePreferenceId}/history`);
    return await this.common.getJSON(url);
  }

  async findOptionsForPracticePreference(preferenceKey: string, query?: string): Promise<string[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/options/${preferenceKey}`, {
      query,
    });
    return await this.common.getJSON(url);
  }

  async create(values: CreateValues): Promise<PracticePreference> {
    const url = this.common.constructPath(this.baseEndpoint);
    return await this.common.postJSON(url, values);
  }

  async update(id: string, values: UpdateValues): Promise<PracticePreference> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.patchJSON(url, values);
  }

  async override(values: {
    attribute: string;
    preferenceIds: string[];
  }): Promise<{ success: true }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/override`);
    return await this.common.patchJSON(url, values);
  }

  async getInherited(
    args: RequireExactlyOne<{
      practiceId: string;
      practiceGroupId: string;
      organizationId: string;
    }>
  ): Promise<Preferences> {
    let basePath;
    if (args.practiceId) {
      basePath = `/practices/${args.practiceId}`;
    } else if (args.practiceGroupId) {
      basePath = `/practice-groups/${args.practiceGroupId}`;
    } else if (args.organizationId) {
      basePath = `/organizations/${args.organizationId}`;
    } else {
      throw new Error('Unknown preference entity');
    }
    const url = this.common.constructPath(`${basePath}/preferences`);
    return await this.common.getJSON(url);
  }
}
